/**
 * The tenant plane's chrome (round9/design-pass — Direction A): a single
 * white topbar with the brand, the four primary destinations (Leads,
 * Settings, Templates, Billing), the business name and Sign out. Rendered
 * ONCE by ProtectedRoute around every tenant page, replacing the per-page
 * <header> each page used to draw for itself (a logo-only header on
 * /leads, a lone "← Back to leads" link everywhere else — no way to reach
 * Billing from Rate Card without going back through Leads first).
 *
 * Why here and not in each page: the same reason ProtectedRoute hosts the
 * trial banner — every tenant route already passes through it, so the
 * chrome cannot drift between pages, and the loading / error branches of a
 * page get the same header as its loaded state (they used to render bare).
 *
 * Active state is computed from the pathname prefix rather than NavLink's
 * exact matching because Settings owns TWO routes (/settings/* and /embed)
 * and Templates owns three (/templates, /templates/new, /templates/:id/edit).
 * aria-current="page" carries the state for assistive tech; the filled
 * background carries it visually (never colour alone).
 */
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/auth-context";
import { isTenantUser } from "../../lib/authRole";
import { homeownerDesignerUrl } from "../../lib/designerLink";
import { DOCKPRO_LOGO_URL } from "../../lib/brand";
import { SkipLink } from "../SkipLink";
import styles from "./TenantShell.module.css";

interface NavItem {
  label: string;
  to: string;
  /** Path prefixes that count as "this section". */
  matches: string[];
}

const NAV_ITEMS: readonly NavItem[] = [
  { label: "Leads", to: "/leads", matches: ["/leads"] },
  { label: "Settings", to: "/settings/rate-card", matches: ["/settings", "/embed", "/rate-card"] },
  { label: "Templates", to: "/templates", matches: ["/templates"] },
  { label: "Billing", to: "/billing", matches: ["/billing"] },
];

function isActive(item: NavItem, pathname: string): boolean {
  return item.matches.some((prefix) => pathname === prefix || pathname.startsWith(prefix + "/"));
}

export function TenantShell({
  businessName,
  banner,
  children,
}: {
  /** The tenant's own business name (branding.builderName), once loaded. */
  businessName?: string | null;
  /** The trial banner, owned by ProtectedRoute; rendered above the topbar. */
  banner?: ReactNode;
  children: ReactNode;
}) {
  const { user, signOut } = useAuth();
  const { pathname } = useLocation();
  // round11/portal-create-lead — the builder's live designer (what their
  // homeowners see), opened in a new tab. Null when the build has no embed
  // configured, in which case the link is simply not offered.
  const designerUrl = user && isTenantUser(user) ? homeownerDesignerUrl(user.tenantId) : null;

  return (
    <div className={styles.shell}>
      <SkipLink targetId="main-content" />
      {banner}
      <header className={styles.topbar}>
        <div className={styles.topbarInner}>
          <Link to="/leads" className={styles.brand} aria-label="DockPro — go to leads">
            <img src={DOCKPRO_LOGO_URL} alt="" className={styles.mark} width={34} height={34} />
            <span className={styles.wordmark}>DockPro</span>
          </Link>
          <nav aria-label="Primary" className={styles.nav}>
            {NAV_ITEMS.map((item) => {
              const active = isActive(item, pathname);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={active ? styles.navLinkActive : styles.navLink}
                  aria-current={active ? "page" : undefined}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <div className={styles.identity}>
            {/* Utility, not primary navigation: it leaves the Portal. Sits
                with the identity cluster so the Primary nav stays the four
                sections a11y tests and the rail contract expect. */}
            {designerUrl && (
              <a
                href={designerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.navLinkExternal}
                aria-label="Open your designer in a new tab"
              >
                Designer<span aria-hidden="true" className={styles.externalMark}>↗</span>
              </a>
            )}
            {businessName && <span className={styles.business}>{businessName}</span>}
            <button type="button" onClick={signOut} className={styles.signOut}>
              Sign out
            </button>
          </div>
        </div>
      </header>
      {children}
    </div>
  );
}
